import React, {useMemo} from 'react';
import {AppTheme} from '../../../../appTheme';
import {
  Accordion,
  ActionIcon,
  Checkbox,
  CloseButton,
  Grid,
  Group,
  NumberInput,
  Paper,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Textarea,
  Center,
  Box
} from '@mantine/core';
import {Item} from '../../../../redux/entities/inventory/item';
import {Category, CategoryTreeNode} from '../../../../redux/entities/inventory/category';
import UseItemAccordionSearch from '../../../../hooks/UseItemAccordionSearch';
import { FieldArray, useFormikContext } from 'formik';
import {OrderItem} from "../../../../redux/entities/inventory/joining/orderItem";
import {createCategoryTree} from '../../../../hooks/useCategoryTree';
import {IconBuildingWarehouse, IconHome, IconTrash} from "@tabler/icons-react";

export type SelectedItem = { quantity: number; notes?: string; checked?: boolean };

type Props = {
  items: Item[];
  categories: Category[];
  // Title to display above the items section (e.g., "Items to Withdraw" or "Items to Export")
  title?: string;
  // Controls which quantity fields are visible. By default, show both.
  showStoreQuantity?: boolean;
  showWarehouseQuantity?: boolean;
  storeQuantityLabel?: string;
  warehouseQuantityLabel?: string;
  thinnerCategories?: boolean;
  hideSourceSelector?: boolean;
};

/**
 * Reusable order item input UI consisting of:
 * - Selected items list with quantity and notes
 * - Search bar to filter items
 * - Inventory accordion (categories -> items) with checkboxes
 */
const OrderItemInput: React.FC<Props> = ({
  items,
  categories,
  title,
  showStoreQuantity = true,
  showWarehouseQuantity = false,
  storeQuantityLabel,
  warehouseQuantityLabel,
  thinnerCategories = false,
  hideSourceSelector = true
}) => {
  // Ensure quantity inputs have the same visual width and wrap horizontally when needed
  const qtyInputWidth = '8rem';
  // Access formik context for orderItems field array (generic to allow reuse across forms)
  const { values, setFieldValue } = useFormikContext<any>();
  // Categorize items by categoryId
  const categorized = useMemo(() => {
    const byCategory: Record<string, Item[]> = {};
    items.forEach(it => {
      const list = byCategory[it.categoryId] ?? (byCategory[it.categoryId] = []);
      list.push(it);
    });
    return byCategory;
  }, [items]);

  // Build a category tree so that only top-level categories render as Accordion items
  const categoryTree = useMemo(() => createCategoryTree(categories), [categories]);

  // Helper: collect items for a node and its descendants
  const getItemsForNode = (node: CategoryTreeNode): Item[] => {
    const here = categorized[node.id] ?? [];
    const children = (node.children || []).flatMap(ch => getItemsForNode(ch));
    return [...here, ...children];
  };

  // Helper: collect all descendant category IDs including self
  const getIdsForNode = (node: CategoryTreeNode): string[] => {
    return [node.id, ...(node.children || []).flatMap(getIdsForNode)];
  };

  // Items that do not belong to any category in the provided categories list
  const uncategorizedItems = useMemo(() => {
    const categoryIds = new Set(categories.map(c => c.id));
    return items.filter(it => !it.categoryId || !categoryIds.has(it.categoryId));
  }, [items, categories]);

  // Hook: handles item search text and which accordion categories are opened
  const { itemSearch, setItemSearch, openedCategories, setOpenedCategories } = UseItemAccordionSearch({
    categories,
    categorized
  });

  const q = itemSearch.trim().toLowerCase();
  const matchedCategoryIds = new Set<string>(
    q.length === 0 ? [] : categories.filter(c => (c.name ?? '').toLowerCase().includes(q)).map(c => c.id)
  );

  // orderItems managed by Formik FieldArray
  const orderItems = values.orderItems ?? [];
  const isFromWarehouse = values.isFromWarehouse ?? true;
  const findIndexByItemId = (itemId: string) => (orderItems || []).findIndex((oi: { itemId: string; }) => oi?.itemId === itemId);

  return (
    <>
      <Grid align="center" gutter="md">
        <Grid.Col span={12} md={7}>
          {title && <h2 style={{color: AppTheme.colors.themeGreen, marginBottom: 0}}>{title}</h2>}
        </Grid.Col>
        {!hideSourceSelector && (
          <Grid.Col span={12} md={5}>
            <Group position="right" align="center" noWrap spacing="sm">
              <Text size="xs" color="dimmed" style={{ textAlign: 'right', lineHeight: 1}}>
                Items coming from:
              </Text>
              <SegmentedControl
                size="sm"
                value={isFromWarehouse ? 'warehouse' : 'store'}
                onChange={(val) => {
                  setFieldValue('isFromWarehouse', val === 'warehouse');
                }}
                styles={{label: {marginBottom: 0}}}
                data={[
                  {
                    value: 'warehouse',
                    label: (
                      <Group spacing={4} noWrap align="center">
                        <IconBuildingWarehouse size="0.8rem" style={{ display: 'block' }} />
                        <Text size="xs">Warehouse</Text>
                      </Group>
                    ),
                  },
                  {
                    value: 'store',
                    label: (
                      <Group spacing={4} noWrap align="center">
                        <IconHome size="0.8rem" style={{ display: 'block' }} />
                        <Text size="xs">Store</Text>
                      </Group>
                    ),
                  }
                ]}
              />
            </Group>
          </Grid.Col>
        )}
        {/* Nested grid to place Browse Items on the left and Selected items on the right */}
        <Grid.Col span={12}>
          <Grid gutter="md">
            {/* Browse Items (left) */}
            <Grid.Col
              span={12}
              md={thinnerCategories ? 5 : 7}
              style={{ maxHeight: '50vh', overflowY: 'auto', overflowX: 'hidden', overflowWrap: 'break-word', paddingRight: 4 }}
            >
              <Text weight={600} style={{color: AppTheme.colors.themeGreen}}>Browse Items</Text>
              <Text size="sm" color="dimmed">Browse available items and specify quantities</Text>
              <TextInput
                placeholder="Search items..."
                value={itemSearch}
                onChange={(e) => setItemSearch(e.currentTarget.value)}
                size="sm"
                label="Search inventory"
                rightSection={itemSearch ? (
                  <CloseButton aria-label="Clear search" onClick={() => setItemSearch('')} />
                ) : undefined}
                style={{ marginTop: 8 }}
              />

              <Accordion
                multiple
                value={openedCategories}
                onChange={(v) => setOpenedCategories(v as string[])}
                variant="contained"
                styles={{
                  item: {
                    borderColor: AppTheme.colors.themeGreen + '33'
                  }
                }}
                style={{ marginTop: 8 }}
              >
                {categoryTree
                  .filter((node) => {
                    const nodeItems = getItemsForNode(node);
                    if (q.length === 0) return nodeItems.length > 0;
                    // If any category in this subtree matches by name
                    const subtreeIds = getIdsForNode(node);
                    if (subtreeIds.some(id => matchedCategoryIds.has(id))) return nodeItems.length > 0;
                    // Or any item in subtree matches by name
                    return nodeItems.some(it => (it.name ?? '').toLowerCase().includes(q));
                  })
                  .map(node => (
                    <Accordion.Item value={node.id} key={node.id}>
                      <Accordion.Control>
                        <Text weight={600} style={{ color: AppTheme.colors.themeGreen }}>{node.name}</Text>
                      </Accordion.Control>
                      <Accordion.Panel>
                        {/* Render items for this category and nested subcategories */}
                        <Grid gutter={0}>
                          {/* Items that belong directly to this category */}
                          {(categorized[node.id] ?? [])
                            .filter(invItem => {
                              if (q.length === 0) return true;
                              if (matchedCategoryIds.has(node.id)) return true;
                              return (invItem.name ?? '').toLowerCase().includes(q);
                            })
                            .map(invItem => {
                              const idx = findIndexByItemId(invItem.id);
                              const isSelected = idx >= 0;
                              const onKeyToggle: React.KeyboardEventHandler<HTMLDivElement> = (ev) => {
                                if (ev.key === 'Enter' || ev.key === ' ') {
                                  ev.preventDefault();
                                  // handled by click handler below
                                }
                              };
                              return (
                                <Grid.Col span={12} key={invItem.id}>
                                  <Paper
                                    withBorder
                                    p="xs"
                                    radius="md"
                                    role="button"
                                    tabIndex={0}
                                    aria-pressed={isSelected}
                                    aria-label={`Select ${invItem.name}`}
                                            onClick={() => {
                                              setFieldValue('orderItems', (() => {
                                                const next = [...orderItems];
                                                if (isSelected) {
                                                  next.splice(idx, 1);
                                                } else {
                                                  next.push({
                                                    id: '',
                                                    orderId: '',
                                                    itemId: invItem.id,
                                                    storeQuantity: 1,
                                                    warehouseQuantity: 0,
                                                    extraComment: '',
                                                    itemOptions: []
                                                  });
                                                }
                                                return next;
                                              })());
                                            }}
                                    onKeyDown={onKeyToggle}
                                    style={{
                                      borderColor: isSelected ? AppTheme.colors.themeGreen : AppTheme.colors.themeGreen + '33',
                                      background: isSelected ? AppTheme.colors.largerOffsetBackground : undefined,
                                      cursor: 'pointer',
                                      outline: 'none'
                                    }}
                                  >
                                    <Group align="center" position="apart" noWrap>
                                      <Text size="sm" style={{ fontWeight: 600 }}>{invItem.name}</Text>
                                      <Text size="xs" color={isSelected ? 'green' : 'dimmed'}>
                                        {isSelected ? 'Selected' : 'Click to select'}
                                      </Text>
                                    </Group>
                                  </Paper>
                                </Grid.Col>
                              );
                            })}
                        </Grid>

                        {/* Recursively render children as nested headers with their items */}
                        {(() => {
                          const renderChild = (child: CategoryTreeNode, depth: number) => {
                            const childItems = categorized[child.id] ?? [];
                            // Determine visibility for search
                            const showChild = (() => {
                              if (q.length === 0) return childItems.length > 0 || (child.children || []).some(grand => getItemsForNode(grand).length > 0);
                              if (matchedCategoryIds.has(child.id)) return (getItemsForNode(child).length > 0);
                              const anyItemMatch = getItemsForNode(child).some(it => (it.name ?? '').toLowerCase().includes(q));
                              return anyItemMatch;
                            })();
                            if (!showChild) return null;
                            return (
                              <div key={child.id} style={{ marginTop: 8 }}>
                                <Text weight={600} style={{ paddingLeft: depth * 12, color: AppTheme.colors.themeGreen }}>{child.name}</Text>
                                <Grid gutter={0} style={{ marginTop: 4, paddingLeft: depth * 12 }}>
                                  {childItems
                                    .filter(invItem => {
                                      if (q.length === 0) return true;
                                      if (matchedCategoryIds.has(child.id)) return true;
                                      return (invItem.name ?? '').toLowerCase().includes(q);
                                    })
                                    .map(invItem => {
                                      const idx = findIndexByItemId(invItem.id);
                                      const isSelected = idx >= 0;
                                      const onKeyToggle: React.KeyboardEventHandler<HTMLDivElement> = (ev) => {
                                        if (ev.key === 'Enter' || ev.key === ' ') {
                                          ev.preventDefault();
                                        }
                                      };
                                      return (
                                        <Grid.Col span={12} key={invItem.id}>
                                          <Paper
                                            withBorder
                                            p="xs"
                                            radius="md"
                                            role="button"
                                            tabIndex={0}
                                            aria-pressed={isSelected}
                                            aria-label={`Select ${invItem.name}`}
                                            onClick={() => {
                                              setFieldValue('orderItems', (() => {
                                                const next = [...orderItems];
                                                if (isSelected) {
                                                  next.splice(idx, 1);
                                                } else {
                                                  next.push({
                                                    id: '',
                                                    orderId: '',
                                                    itemId: invItem.id,
                                                    storeQuantity: 1,
                                                    warehouseQuantity: 0,
                                                    extraComment: '',
                                                    itemOptions: []
                                                  });
                                                }
                                                return next;
                                              })());
                                            }}
                                            onKeyDown={onKeyToggle}
                                            style={{
                                              borderColor: isSelected ? AppTheme.colors.themeGreen : AppTheme.colors.themeGreen + '33',
                                              background: isSelected ? AppTheme.colors.largerOffsetBackground : undefined,
                                              cursor: 'pointer',
                                              outline: 'none'
                                            }}
                                          >
                                            <Group align="center" position="apart" noWrap>
                                              <Text size="sm" style={{ fontWeight: 600 }}>{invItem.name}</Text>
                                              <Text size="xs" color={isSelected ? 'green' : 'dimmed'}>
                                                {isSelected ? 'Selected' : 'Click to select'}
                                              </Text>
                                            </Group>
                                          </Paper>
                                        </Grid.Col>
                                      );
                                    })}
                                </Grid>
                                {(child.children || []).map(grand => renderChild(grand, depth + 1))}
                              </div>
                            );
                          };
                          return (node.children || []).map(ch => renderChild(ch, 1));
                        })()}
                      </Accordion.Panel>
                    </Accordion.Item>
                  ))}

                {/* Other (uncategorized) section */}
                {(() => {
                  const filtered = uncategorizedItems.filter(invItem => {
                    if (q.length === 0) return true;
                    return (invItem.name ?? '').toLowerCase().includes(q);
                  });
                  if (filtered.length === 0) return null;
                  const OTHER_KEY = '__OTHER__';
                  return (
                    <Accordion.Item value={OTHER_KEY} key={OTHER_KEY}>
                      <Accordion.Control>
                        <Text weight={600} style={{ color: AppTheme.colors.themeGreen }}>Other</Text>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Grid gutter={0}>
                          {filtered.map(invItem => {
                            const idx = findIndexByItemId(invItem.id);
                            const isSelected = idx >= 0;
                            const onKeyToggle: React.KeyboardEventHandler<HTMLDivElement> = (ev) => {
                              if (ev.key === 'Enter' || ev.key === ' ') {
                                ev.preventDefault();
                                // handled by click handler below
                              }
                            };
                            return (
                              <Grid.Col span={12} key={invItem.id}>
                                <Paper
                                  withBorder
                                  p="xs"
                                  radius="md"
                                  role="button"
                                  tabIndex={0}
                                  aria-pressed={isSelected}
                                  aria-label={`Select ${invItem.name}`}
                                  onClick={() => {
                                    setFieldValue('orderItems', (() => {
                                      const next = [...orderItems];
                                      if (isSelected) {
                                        next.splice(idx, 1);
                                      } else {
                                        next.push({
                                          id: '',
                                          orderId: '',
                                          itemId: invItem.id,
                                          storeQuantity: 1,
                                          warehouseQuantity: 0,
                                          extraComment: '',
                                          itemOptions: []
                                        });
                                      }
                                      return next;
                                    })());
                                  }}
                                  onKeyDown={onKeyToggle}
                                  style={{
                                    borderColor: isSelected ? AppTheme.colors.themeGreen : AppTheme.colors.themeGreen + '33',
                                    background: isSelected ? AppTheme.colors.largerOffsetBackground : undefined,
                                    cursor: 'pointer',
                                    outline: 'none'
                                  }}
                                >
                                  <Group align="center" position="apart" noWrap>
                                    <Text size="sm" style={{ fontWeight: 600 }}>{invItem.name}</Text>
                                    <Text size="xs" color={isSelected ? 'green' : 'dimmed'}>
                                      {isSelected ? 'Selected' : 'Click to select'}
                                    </Text>
                                  </Group>
                                </Paper>
                              </Grid.Col>
                            );
                          })}
                        </Grid>
                      </Accordion.Panel>
                    </Accordion.Item>
                  );
                })()}
              </Accordion>
            </Grid.Col>

            {/* Selected Items (right) */}
            <Grid.Col
              span={12}
              md={thinnerCategories ? 7 : 5}
              style={{ maxHeight: '50vh', overflowY: 'auto', overflowX: 'hidden', overflowWrap: 'break-word', paddingRight: 4 }}
            >
              <Text weight={600} style={{ color: AppTheme.colors.themeGreen }}>Selected items</Text>
              <Text size="sm" color="dimmed">Selected items from the Browse Items section will appear here</Text>
              <FieldArray name="orderItems">
                {(arrayHelpers) => (
                  (orderItems.length === 0 ? (
                <Text size="sm" color="dimmed" style={{ marginTop: 8 }}>No items selected yet.</Text>
              ) : (
                    <Grid gutter="xs" mt="xs">
                      {orderItems.map((oi: OrderItem, index: number) => {
                        const invItem = items.find(i => i.id === oi.itemId);
                        return (
                          <Grid.Col span={12} key={index}>
                            <Paper
                              withBorder
                              p="sm"
                              radius="md"
                              shadow="xs"
                              style={{
                                borderColor: AppTheme.colors.themeGreen + '33',
                                transition: 'all 0.2s ease',
                                padding: '1rem'
                              }}
                              sx={{
                                '&:hover': {
                                  borderColor: AppTheme.colors.themeGreen,
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                                }
                              }}
                            >
                              <Grid gutter="xs" align="center">
                                <Grid.Col span={12} xs={6}>
                                  <Stack spacing={4} sx={{ flex: 1, width: '100%' }}>
                                    <Text size="md" weight={600} style={{ lineHeight: 1.2 }}>
                                      {invItem?.name}
                                    </Text>
                                    <Textarea
                                      placeholder="Add notes..."
                                      autosize
                                      minRows={1}
                                      maxRows={3}
                                      size="xs"
                                      variant="filled"
                                      value={oi.extraComment ?? ''}
                                      onChange={(e) => setFieldValue(`orderItems[${index}].extraComment`, e.currentTarget.value)}
                                      styles={{
                                        input: {
                                          fontSize: '0.75rem',
                                          minHeight: 'unset',
                                          backgroundColor: AppTheme.colors.largerOffsetBackground,
                                          border: 'none'
                                        }
                                      }}
                                    />
                                  </Stack>
                                </Grid.Col>

                                <Grid.Col span={12} xs={6}>
                                  <Group noWrap align="flex-end" spacing="xs" style={{ justifyContent: 'flex-end', height: '100%' }}>
                                    <Group spacing={4} noWrap align="flex-end" position="right" style={{ justifyContent: 'flex-end', flex: 1 }}>
                                      {showWarehouseQuantity && (
                                        <NumberInput
                                          size="xs"
                                          label={warehouseQuantityLabel ?? 'Whse Qty'}
                                          min={0}
                                          value={oi.warehouseQuantity ?? 0}
                                          onChange={(val) => {
                                            const num = typeof val === 'number' ? val : 0;
                                            setFieldValue(`orderItems[${index}].warehouseQuantity`, num);
                                          }}
                                          styles={{
                                            label: { color: AppTheme.colors.primaryTextColor, fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 },
                                            input: { width: '3.8rem', textAlign: 'center', padding: '0 4px' }
                                          }}
                                        />
                                      )}
                                      {showStoreQuantity && (
                                        <NumberInput
                                          size="xs"
                                          label={storeQuantityLabel ?? (invItem?.restrictOrderSize && invItem.maxOrderSize > 0 ? `${showWarehouseQuantity ? 'Store' : ''} (Max ${invItem?.maxOrderSize})` : (showWarehouseQuantity ? 'Store Qty' : 'Qty'))}
                                          min={0}
                                          value={oi.storeQuantity ?? 1}
                                          onChange={(val) => {
                                            const num = typeof val === 'number' ? val : 0;
                                            setFieldValue(`orderItems[${index}].storeQuantity`, num);
                                          }}
                                          styles={{
                                            label: { color: AppTheme.colors.primaryTextColor, fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 },
                                            input: { width: '3.8rem', textAlign: 'center', padding: '0 4px' }
                                          }}
                                        />
                                      )}
                                    </Group>

                                    <Box sx={{ display: 'flex', alignItems: 'center', height: 30, marginBottom: 2 }}>
                                      <ActionIcon
                                        color="red"
                                        variant="subtle"
                                        size="sm"
                                        onClick={() => arrayHelpers.remove(index)}
                                        title={`Remove ${invItem?.name}`}
                                      >
                                        <IconTrash size="1rem" />
                                      </ActionIcon>
                                    </Box>
                                  </Group>
                                </Grid.Col>
                              </Grid>
                            </Paper>
                          </Grid.Col>
                        );
                      })}
                    </Grid>
                  ))
                )}
              </FieldArray>
            </Grid.Col>
          </Grid>
        </Grid.Col>
      </Grid>
    </>
  );
};

export default OrderItemInput;
